import type { AuthProvider, SignInResult, SignUpInput } from './types';

/**
 * SOLO SVILUPPO LOCALE: simula Cognito nel browser e firma token HS256 accettati dall'API in AUTH_MODE=dev.
 * Codice di verifica e 2FA: sempre 123456.
 */
const KEY = 'yardpay.dev.session';
const USERS = 'yardpay.dev.users';

interface DevUser { email: string; password: string; firstName: string; lastName: string; sub: string; verified: boolean; mfa: boolean }

function b64url(data: ArrayBuffer | string): string {
  const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : new Uint8Array(data);
  let s = '';
  bytes.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export class DevAuth implements AuthProvider {
  private pendingEmail: string | null = null;
  constructor(private secret: string) {}

  private users(): Record<string, DevUser> {
    try { return JSON.parse(localStorage.getItem(USERS) ?? '{}'); } catch { return {}; }
  }
  private save(u: DevUser): void {
    const all = this.users();
    all[u.email] = u;
    localStorage.setItem(USERS, JSON.stringify(all));
  }
  private check(code: string): void {
    if (code.trim() !== '123456') throw Object.assign(new Error('Codice non corretto'), { code: 'CodeMismatchException' });
  }

  async signUp(i: SignUpInput): Promise<void> {
    const email = i.email.trim().toLowerCase();
    if (this.users()[email]) throw Object.assign(new Error('exists'), { code: 'UsernameExistsException' });
    this.save({ email, password: i.password, firstName: i.firstName, lastName: i.lastName, sub: crypto.randomUUID(), verified: false, mfa: false });
  }
  async confirmSignUp(email: string, code: string): Promise<void> {
    this.check(code);
    const u = this.users()[email.trim().toLowerCase()];
    if (u) this.save({ ...u, verified: true });
  }
  async resendSignUpCode(): Promise<void> {}
  async signIn(email: string, password: string): Promise<SignInResult> {
    const u = this.users()[email.trim().toLowerCase()];
    if (!u || u.password !== password) throw Object.assign(new Error('x'), { code: 'NotAuthorizedException' });
    if (!u.verified) return { status: 'email_not_verified' };
    this.pendingEmail = u.email;
    return { status: u.mfa ? 'mfa_required' : 'mfa_setup_required' };
  }
  async submitMfaCode(code: string): Promise<SignInResult> {
    this.check(code);
    localStorage.setItem(KEY, this.pendingEmail ?? '');
    return { status: 'signed_in' };
  }
  async beginTotpSetup(): Promise<{ secret: string; uri: string }> {
    return { secret: 'DEVSECRETDEVSECRET', uri: 'otpauth://totp/Yardpay:dev?secret=DEVSECRETDEVSECRET&issuer=Yardpay' };
  }
  async verifyTotpSetup(code: string): Promise<SignInResult> {
    this.check(code);
    const u = this.pendingEmail ? this.users()[this.pendingEmail] : undefined;
    if (u) this.save({ ...u, mfa: true });
    localStorage.setItem(KEY, this.pendingEmail ?? '');
    return { status: 'signed_in' };
  }
  async forgotPassword(): Promise<void> {}
  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    this.check(code);
    const u = this.users()[email.trim().toLowerCase()];
    if (u) this.save({ ...u, password: newPassword });
  }
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const u = this.users()[this.currentEmail() ?? ''];
    if (!u || u.password !== oldPassword) throw Object.assign(new Error('x'), { code: 'NotAuthorizedException' });
    this.save({ ...u, password: newPassword });
  }
  currentEmail(): string | null {
    return localStorage.getItem(KEY) || null;
  }
  async getIdToken(): Promise<string | null> {
    const u = this.users()[this.currentEmail() ?? ''];
    if (!u) return null;
    const now = Math.floor(Date.now() / 1000);
    const header = b64url(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const payload = b64url(JSON.stringify({
      sub: u.sub, email: u.email, email_verified: true, given_name: u.firstName, family_name: u.lastName,
      token_use: 'id', iss: 'yardpay-dev', iat: now, exp: now + 3600,
    }));
    const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(this.secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(`${header}.${payload}`));
    return `${header}.${payload}.${b64url(sig)}`;
  }
  async signOut(): Promise<void> {
    localStorage.removeItem(KEY);
  }
}
